/* eslint-disable prettier/prettier */
/* eslint-disable arrow-parens */
import { useState, useEffect, useContext, useCallback, Suspense, useRef } from "react";
import { TextField, Paper, Grid, makeStyles, Backdrop, Button } from "@material-ui/core";
import { Autocomplete, SpeedDial, SpeedDialIcon, SpeedDialAction, Alert, AlertTitle } from "@material-ui/lab";

import { Link, Element, Events, animateScroll as scroll, scroller } from "react-scroll";

import { LazyChangeLogo, LazyLogoResizing, LazyTheme } from "../..";

import PublishIcon from "@material-ui/icons/Publish";
import UndoIcon from "@material-ui/icons/Undo";
import VisibilityIcon from "@material-ui/icons/Visibility";
import RestoreIcon from "@material-ui/icons/Restore";

import "./employeePageDesign.css";
import { AuthContext, UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import { LazyCustomDialog, LazySnackbarAPI } from "../../../shared";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import useRouteParam from "../../../../hooks/useRouteParam";
import { trackPromise } from "react-promise-tracker";
import { MEMBER_PORTAL_REDIRECTION } from "../../../../config/config";
import { getPermissionsFromGroupOwner } from "../../../../utils/getPermissionsFromGroupOwner";

const useStyles = makeStyles((theme) => ({
	root: {
		height: 380,
		transform: "translateZ(0px)",
		flexGrow: 1
	},
	alert: {
		width: "100%",
		"& > * + *": {
			marginTop: theme.spacing(2)
		}
	},
	speedDial: {
		position: "fixed",
		bottom: theme.spacing(2),
		right: theme.spacing(2)
	}
}));

const EmployeePageDesign = () => {
	const classes = useStyles();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroupNumber, setSelectedGroupNumber] = useState();
	const [groups, setGroups] = useState([]);
	const [selectedGroup, setSelectedGroup] = useState({});
	const [logoSettings, setLogoSettings] = useState({});
	const [hasPreviewed, setHasPreviewed] = useState(false);
	const [theme, setTheme] = useState({});
	const [activeOption, setActiveOption] = useState("Logo and Favicon");
	const [openSpeedDial, setOpenSpeedDial] = useState(false);
	const [dialogProps, setDialogProps] = useState({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState({
		open: false,
		severity: "",
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});
	const [valueChange, setValueChange] = useState(false);
	const { setDashboardHeader } = useContext(UIContext);

	const { user, setUser } = useContext(AuthContext);

	const employeePageDesignPermission = user
		? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.employer_settings_menu
			.custom_employee_page_design_sub.self
		: null;

	// console.log(employeePageDesignPermission, "test", getPermissionsFromGroupOwner(user, Number(queryGroupNumber)));

	const handleGroupSelect = (event, newValue) => setSelectedGroup(newValue);

	const handleOpenSpeedDial = () => setOpenSpeedDial(true);

	const handleCloseSpeedDial = () => setOpenSpeedDial(false);

	const handleLogoFileChange = useCallback(
		(event) => {
			const { name, files } = event.target;

			if (name === "logo") {
				let _logoSettings = logoSettings;
				_logoSettings.logo.attachment = files[0];
				setLogoSettings(Object.assign({}, _logoSettings));
			} else if (name === "favicon") {
				setLogoSettings(
					Object.assign({}, logoSettings, {
						favicon: files[0]
					})
				);
			}
			setValueChange(true);
			event.target.value = "";
		},
		[logoSettings]
	);

	const handleImageDelete = useCallback(
		(name) => {
			//const { name, files } = event.target;
			if (name === "logo") {
				let _logoSettings = logoSettings;
				_logoSettings.logo.attachment = null;
				setLogoSettings(Object.assign({}, _logoSettings));
			} else if (name === "favicon") {
				setLogoSettings(
					Object.assign({}, logoSettings, {
						favicon: null
					})
				);
			}
		},
		[logoSettings]
	);

	const handleThemeStyleChange = useCallback(
		(event) => {
			const { value } = event.target;
			setTheme(Object.assign({}, theme, { theme_style: value }));
		},
		[theme]
	);

	const handleColorChange = useCallback(
		(color, event, name, parent = null) => {
			setValueChange(true);
			if (!parent) {
				setTheme(
					Object.assign({}, theme, {
						[name]: color.hex
					})
				);
				return;
			}
			const _theme = theme;
			_theme[parent] = Object.assign({}, _theme[parent], {
				[name]: color.hex
			});
			setTheme(Object.assign({}, _theme));
		},
		[theme]
	);

	const handleColorChangeComplete = useCallback(
		(color, event, name, parent = null) => {
			setValueChange(true);
			if (!parent) {
				setTheme(
					Object.assign({}, theme, {
						[name]: color.hex
					})
				);
				return;
			}
			const _theme = theme;
			_theme[parent] = Object.assign({}, _theme[parent], {
				[name]: color.hex
			});
			setTheme(Object.assign({}, _theme));
		},
		[theme]
	);

	const handleLogoSizeChange = useCallback(
		(event, newValue, screenId, name) => {
			setValueChange(true);
			let _logoSettings = { ...logoSettings };
			_logoSettings.logo[screenId] = Object.assign({}, _logoSettings.logo[screenId], {
				[name]: newValue
			});
			setLogoSettings(Object.assign({}, { ..._logoSettings }));
		},
		[logoSettings]
	);

	const handleResetToDefault = useCallback(() => {
		let openDialog = true;
		let title = "Reset to default";
		let content = "Are you sure you want to reset to default?";

		let actions = [
			{
				label: "Cancel",
				callback: () => setDialogProps(Object.assign({}, dialogProps, { openDialog: false }))
			},
			{
				label: "Yes",
				callback: async () => {
					const _theme = {
						primary_color: "#e0e0e0",
						secondary_color: "#4e4e4e",
						button: {
							background_color: "#e0e0e0",
							color: "#000000"
						},
						top_bar: {
							color: "#ffffff",
							background_color: "#7e7e7e"
						},
						group_number: selectedGroupNumber
					};
					if (theme._id) {
						setTheme(
							Object.assign(
								{},
								{
									_id: theme._id,
									..._theme
								}
							)
						);
					} else {
						setTheme(Object.assign({}, _theme));
					}
					const _logoSettings = {
						logo: {
							attachment: null,
							xl: {
								height: 200,
								width: 100
							},
							lg: {
								height: 200,
								width: 100
							},
							md: {
								height: 200,
								width: 100
							},
							sm: {
								height: 200,
								width: 100
							},
							xs: {
								height: 200,
								width: 100
							}
						},
						favicon: null,
						group_number: selectedGroupNumber
					};
					if (logoSettings._id) {
						setLogoSettings(
							Object.assign(
								{},
								{
									_id: logoSettings._id,
									...logoSettings
								}
							)
						);
					} else {
						setLogoSettings(Object.assign({}, _logoSettings));
					}
					setDialogProps(Object.assign({}, dialogProps, { openDialog: false }));
				}
			}
		];
		setDialogProps(
			Object.assign({}, dialogProps, {
				openDialog,
				title,
				content,
				actions
			})
		);
	}, [dialogProps, logoSettings, selectedGroupNumber, theme._id]);

	const scrollToTop = () => scroll.scrollToTop();

	const scrollToBottom = () => scroll.scrollToBottom();

	const scrollTo = (element) => {
		scroller.scrollTo(element, {
			duration: 500,
			delay: 0,
			smooth: "easeInOutQuart"
		});
	};

	const getGroups = useCallback(async () => {
		const response = await trackPromise(api.assignGroupsAndLocation.getAssignedGroups());
		// console.log("_groupss", response.groups);
		setGroups(Object.assign([], [...response.groups]));
		setSelectedGroup(Object.assign({}, response.groups[response.groups.length - 1]));
	}, []);

	const handlePublishThemeColor = useCallback(async () => {
		let _theme;
		try {
			if (theme && theme._id) {
				_theme = await trackPromise(api.employeePageDesign.editThemeColor(theme));
			} else {
				_theme = await trackPromise(api.employeePageDesign.addThemeColor(theme));
			}
			setTheme(Object.assign({}, _theme));
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: true,
					message: "Successfully published changes",
					severity: "success"
				})
			);
		} catch (err) {
			console.log("err", err);
		}
	}, [snackbarAPICallProps, theme]);

	const getLogoSettings = useCallback(async (group_number) => {
		const _logoSettings = await trackPromise(api.employeePageDesign.getLogoSettingsByGroupNumber(group_number));
		console.log("_logoSettings", _logoSettings);
		if (_logoSettings === false) {
			setLogoSettings(
				Object.assign(
					{},
					{
						logo: {
							attachment: null,
							xl: {
								height: 200,
								width: 100
							},
							lg: {
								height: 200,
								width: 100
							},
							md: {
								height: 200,
								width: 100
							},
							sm: {
								height: 200,
								width: 100
							},
							xs: {
								height: 200,
								width: 100
							}
						},
						favicon: null,
						group_number
					}
				)
			);
			return;
		}
		setLogoSettings(Object.assign({}, _logoSettings));
	}, []);

	const getTheme = useCallback(async (group_number) => {
		const _theme = await trackPromise(api.employeePageDesign.getThemeColorByGroupNumber(group_number));
		if (_theme === false) {
			setTheme(
				Object.assign(
					{},
					{
						theme_style: "theme_style_1",
						primary_color: "#e0e0e0",
						secondary_color: "#4e4e4e",
						button: {
							background_color: "#e0e0e0",
							color: "#000000"
						},
						top_bar: {
							color: "#fffff",
							background_color: "#7e7e7e"
						},
						group_number
					}
				)
			);
			return;
		} else setTheme(Object.assign({}, _theme));
	}, []);

	const handleUndoChanges = useCallback(() => {
		let openDialog = true;
		let title = "Undo Changes";
		let content = "Are you sure you want to undo your changes?";

		let actions = [
			{
				label: "Cancel",
				callback: () => setDialogProps(Object.assign({}, dialogProps, { openDialog: false }))
			},
			{
				label: "Yes",
				callback: () => {
					getTheme(selectedGroupNumber);
					getLogoSettings(selectedGroupNumber);
					setDialogProps(Object.assign({}, dialogProps, { openDialog: false }));
				}
			}
		];
		setDialogProps(
			Object.assign({}, dialogProps, {
				openDialog,
				title,
				content,
				actions
			})
		);
	}, [dialogProps, getLogoSettings, getTheme, selectedGroupNumber]);

	const handlePublishLogo = useCallback(async () => {
		let formData = new FormData(),
			_logoSettings;
		formData.append("logo_settings", JSON.stringify(logoSettings));
		if (logoSettings.logo.attachment) {
			formData.append("logo_attachment", logoSettings.logo.attachment);
		}
		if (logoSettings.favicon) {
			formData.append("favicon", logoSettings.favicon);
		}
		if (logoSettings._id) {
			_logoSettings = await trackPromise(api.employeePageDesign.editLogoSettings(formData));
			setLogoSettings(Object.assign({}, _logoSettings));
		} else {
			_logoSettings = await trackPromise(api.employeePageDesign.addLogoSettings(formData));
		}
		setLogoSettings(Object.assign({}, _logoSettings));
	}, [logoSettings]);

	const handlePreviewLogo = useCallback(async () => {
		let formData = new FormData(),
			_logoSettings;
		formData.append("logo_settings", JSON.stringify(logoSettings));
		formData.append("logo_attachment", logoSettings.logo.attachment);
		formData.append("favicon", logoSettings.favicon);
		_logoSettings = await trackPromise(api.logoSettingsPreview.addLogoSettings(formData));
		if (!_logoSettings) {
			_logoSettings = await trackPromise(api.logoSettingsPreview.editLogoSettings(formData));
		}
		return _logoSettings;
	}, [logoSettings]);

	const handlePreviewThemeColor = useCallback(async () => {
		let _themeColorPreview;
		_themeColorPreview = await trackPromise(api.employeePageDesignPreview.addThemeColor(theme));
		if (_themeColorPreview === false) {
			_themeColorPreview = await trackPromise(api.employeePageDesignPreview.editThemeColor(theme));
		}
		return _themeColorPreview;
	}, [theme]);

	const handlePublishClick = useCallback(async () => {
		if (!logoSettings.logo.attachment || !logoSettings.favicon) {
			alert("Logo and Favicon cannot be empty");
			return;
		}
		await handlePublishLogo();
		await handlePublishThemeColor();
	}, [handlePublishLogo, handlePublishThemeColor, logoSettings]);

	const deletePreviews = useCallback(async () => {
		await api.logoSettingsPreview.deleteLogoSettingsByUserId();
		await api.employeePageDesignPreview.deleteLogoSettingsByUserId();
	}, []);

	const handlePreviewClick = useCallback(async () => {
		if (!logoSettings.logo.attachment || !logoSettings.favicon) {
			alert("Logo and Favicon cannot be empty");
			return;
		}
		setHasPreviewed(true);
		const { user_id } = await handlePreviewLogo();
		await handlePreviewThemeColor();
		// const baseUrl = MEMBER_LINKS[employee.group_number];
		const baseUrl = `${MEMBER_PORTAL_REDIRECTION}/verify`;
		const url = `${baseUrl}/preview?user=${user_id}&&group_number=${selectedGroupNumber}`;
		const target = "_blank";
		window.open(url, target);
	}, [handlePreviewLogo, handlePreviewThemeColor, logoSettings, selectedGroupNumber]);

	useEffect(() => {
		if (queryGroupNumber) setSelectedGroupNumber(queryGroupNumber);
	}, [queryGroupNumber]);

	useEffect(() => {
		Events.scrollEvent.register("begin", function () {
			//console.log("begin", arguments);
		});

		Events.scrollEvent.register("end", function () {
			//console.log("end", arguments);
		});
		// getGroups();
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employee_page_design);

		return () => {
			Events.scrollEvent.remove("begin");
			Events.scrollEvent.remove("end");
		};
	}, [getTheme, setDashboardHeader]);

	const getEmployeePageDesignData = useCallback(async () => {
		const getData = () => {
			if (selectedGroupNumber) {
				getTheme(selectedGroupNumber);
				getLogoSettings(selectedGroupNumber);
			}
		};
		getData();
	}, [getLogoSettings, getTheme, selectedGroupNumber]);

	useEffect(() => {
		getEmployeePageDesignData();
	}, [getEmployeePageDesignData]);

	useEffect(() => {
		deletePreviews();

		//return deletePreviews();
	}, [deletePreviews]);
	// console.log("group", groups);
	return (
		<div className="employee-page-design">
			{employeePageDesignPermission && !employeePageDesignPermission.update && (
				<div style={{ textAlign: "center", fontSize: "21px" }}>No update permission</div>
			)}
			<Suspense fallback={<div />}>
				<LazySnackbarAPI snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={dialogProps} />
			</Suspense>
			<form encType="multipart/form-data">
				<Grid container spacing={1} className="page-design-container">
					{!selectedGroupNumber ? (
						<div>
							<Alert severity="error">
								<AlertTitle>Error</AlertTitle>
								<strong>Select a group</strong> to edit design scheme
							</Alert>
						</div>
					) : (
						<Grid item xl={8} lg={8} className="design-scheme-container">
							{/* <div className="group-name">{selectedGroup && selectedGroup.name}</div> */}
							<div className="scroll-link-element">
								<Element name="logo-upload" id="logo-upload" className="logo-upload">
									<div className="title">Logo Upload</div>
									<Suspense fallback={<div />}>
										<Paper className="settings-container" variant="outlined">
											<LazyChangeLogo
												logoSettings={logoSettings}
												handleLogoFileChange={handleLogoFileChange}
												handleImageDelete={handleImageDelete}
											/>
										</Paper>
									</Suspense>
								</Element>
							</div>
							<div className="scroll-link-element">
								<Element name="logo-resizing" id="logo-resizing" className="logo-resizing">
									<div className="title">Logo Resizing</div>
									<Paper className="settings-container" variant="outlined">
										<Suspense fallback={<div />}>
											<LazyLogoResizing
												logoSettings={logoSettings}
												handleLogoSizeChange={handleLogoSizeChange}
											/>
										</Suspense>
									</Paper>
								</Element>
							</div>
							<div className="scroll-link-element">
								<Element name="theme-color" id="theme-color" className="theme-color">
									<div className="title">Theme</div>
									<Paper className="settings-container" variant="outlined">
										<Suspense fallback={<div />}>
											<LazyTheme
												theme={theme}
												handleLogoSizeChange={handleLogoSizeChange}
												handleColorChange={handleColorChange}
												handleColorChangeComplete={handleColorChangeComplete}
												handleThemeStyleChange={handleThemeStyleChange}
											/>
										</Suspense>
									</Paper>
								</Element>
							</div>
						</Grid>
					)}
					<Grid item xl={2} lg={2} className="vertical-navigation">
						{/* <div className="select-group-container">
							<Autocomplete
								id="combo-box-demo"
								value={selectedGroup}
								onChange={(event, newValue) => handleGroupSelect(event, newValue)}
								options={groups}
								getOptionLabel={(group) => {
									// console.log("labellll", group);
									return group?.name;
								}}
								style={{ width: 300 }}
								renderInput={(params) => (
									<TextField {...params} label="Select Group" variant="outlined" />
								)}
							/>
						</div> */}
						<div className="smooth-scroll-nav">
							<div className="options-header">Options</div>
							<ul className="list nav navbar-nav">
								<li className={activeOption === "Logo and Favicon" ? "active" : ""}>
									<Link
										spy={true}
										smooth={true}
										duration={500}
										onClick={() => {
											setActiveOption("Logo and Favicon");
											scrollToTop();
										}}
									>
										Logo and Favicon
									</Link>
								</li>
								<li className={activeOption === "Logo Resizing" ? "active" : ""}>
									<Link
										spy={true}
										smooth={true}
										duration={500}
										onClick={() => {
											setActiveOption("Logo Resizing");
											scrollTo("logo-resizing");
										}}
									>
										Logo Resizing
									</Link>
								</li>
								<li className={activeOption === "Theme Color" ? "active" : ""}>
									<Link
										spy={true}
										smooth={true}
										duration={500}
										onClick={() => {
											setActiveOption("Theme Color");
											scrollToBottom();
										}}
									>
										Theme Color
									</Link>
								</li>
							</ul>
						</div>
					</Grid>
				</Grid>
			</form>

			<Backdrop open={openSpeedDial} style={{ zIndex: 1009 }} />
			<Button
				variant="contained"
				className="button-green"
				onClick={handlePublishClick}
				startIcon={<PublishIcon />}
				style={{
					position: "fixed",
					bottom: 25,
					right: 80,
					zIndex: 1010
				}}
			>
				Publish
			</Button>

			{employeePageDesignPermission && employeePageDesignPermission.update && (
				<div>
					<SpeedDial
						ariaLabel="SpeedDial tooltip example"
						className={valueChange ? `${classes.speedDial} bounce2` : ` ${classes.speedDial}`}
						icon={<SpeedDialIcon />}
						onClose={handleCloseSpeedDial}
						onOpen={handleOpenSpeedDial}
						open={openSpeedDial}
					>
						<SpeedDialAction
							icon={<PublishIcon />}
							tooltipTitle="Publish"
							tooltipOpen
							onClick={() => {
								handlePublishClick();
								handleCloseSpeedDial();
							}}
						/>
						<SpeedDialAction
							icon={<VisibilityIcon />}
							tooltipTitle="Preview"
							tooltipOpen
							onClick={() => {
								handlePreviewClick();
								handleCloseSpeedDial();
							}}
						/>
						<SpeedDialAction
							icon={<RestoreIcon />}
							tooltipTitle="Reset to default"
							tooltipOpen
							onClick={() => {
								handleResetToDefault();
								handleCloseSpeedDial();
							}}
						/>
						<SpeedDialAction
							icon={<UndoIcon />}
							tooltipTitle="Undo"
							tooltipOpen
							onClick={() => {
								handleUndoChanges();
								handleCloseSpeedDial();
							}}
						/>
					</SpeedDial>
				</div>
			)}
		</div>
	);
};

export default EmployeePageDesign;
